import { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import type { OverviewMap as OLOverviewMap } from 'ol/control';
import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon, Tooltip } from '@/ui';
import { logger } from '@/core/utils/logger';
import { Box } from '@/ui/layout';
import { getSxClasses } from './overview-map-toggle-styles';

/** Properties for the overview map toggle. */
interface OverviewMapToggleProps {
  /** OpenLayers overview map control. */
  overviewMap: OLOverviewMap;
}

/** Static styles for the overview toggle button. */
const OVERVIEW_STYLES = {
  margin: 0,
  padding: 0,
  height: 'initial',
  minWidth: 'initial',
} as const;

/**
 * Creates a toggle icon button for the overview map.
 *
 * @param props - The overview map toggle properties
 * @returns The toggle icon button or null if not yet mounted
 */
export function OverviewMapToggle(props: OverviewMapToggleProps): JSX.Element | null {
  // Component props
  const { overviewMap } = props;

  // Hook
  const { t } = useTranslation<string>();
  const tooltipAndAria = t('mapctrl.overviewmap.toggle');
  const sxClasses = useMemo(() => getSxClasses(), []);

  // State
  const [isExpanded, setIsExpanded] = useState(true);
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(null);

  /**
   * Handles when the user clicks the toggle button to expand or collapse the overview map.
   */
  const handleClick = useCallback((): void => {
    const isCollapsed = overviewMap.getCollapsed();
    setIsExpanded(!isCollapsed);

    const overviewMapViewport = overviewMap.getOverviewMap().getTargetElement() as HTMLDivElement;

    if (overviewMapViewport) {
      if (!isCollapsed) {
        overviewMapViewport.style.width = '150px';
        overviewMapViewport.style.height = '150px';
      } else {
        overviewMapViewport.style.width = '40px';
        overviewMapViewport.style.height = '40px';
        overviewMapViewport.style.margin = '0px';
      }
    }
  }, [overviewMap]);

  /**
   * Polls for the overview map button element and attaches the click handler.
   */
  useEffect(() => {
    logger.logTraceUseEffect('OVERVIEW-MAP-TOGGLE - mount');
    let isCleanedUp = false;

    const intervalId = setInterval(() => {
      if (isCleanedUp) return;
      const overviewMapElement = overviewMap.getOverviewMap().getTargetElement();
      if (!overviewMapElement) return;

      const button = overviewMapElement.parentElement?.querySelector('button');
      if (!button) return;

      // Find the div inside the button
      const buttonDiv = button.querySelector('div');
      if (!buttonDiv) return;

      // Found the button, clear interval and set up events
      clearInterval(intervalId);
      button.setAttribute('aria-label', tooltipAndAria);
      button.addEventListener('click', handleClick);
      setTargetElement(buttonDiv);
    }, 100);

    // Cleanup function to remove event listener
    return (): void => {
      logger.logTraceUseEffectUnmount('OVERVIEW-MAP-TOGGLE - unmount');
      isCleanedUp = true;
      clearInterval(intervalId);
      const button = targetElement?.parentElement;
      if (button) {
        button.removeEventListener('click', handleClick);
      }
    };
  }, [handleClick, tooltipAndAria, overviewMap, targetElement]);

  if (!targetElement) return null;

  return createPortal(
    <Tooltip title={tooltipAndAria}>
      <Box sx={sxClasses.toggleBtnContainer}>
        <Box component="div" sx={sxClasses.toggleBtn} className={isExpanded ? `minimapOpen` : `minimapClosed`} style={OVERVIEW_STYLES}>
          <ChevronLeftIcon />
        </Box>
      </Box>
    </Tooltip>,
    targetElement
  );
}
